/**
 * Centralized pricing plan definitions
 * Simple 3-plan structure: Monthly, Annual, Lifetime
 */

import { appConfigs } from '@/appConfigs.js'

/**
 * Format price with currency
 * @param {number} price - The price value
 * @param {string} currency - The currency code (default: 'USD')
 * @returns {string} - Formatted price string
 */
export function formatPrice(price, currency = 'CAD') {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(price)
}

/**
 * Calculate annual savings percentage compared to monthly
 * @param {number} monthlyPrice - Monthly price
 * @param {number} annualPrice - Annual price
 * @returns {number} - Savings percentage rounded to nearest integer
 */
export function getSavingsPercent(monthlyPrice, annualPrice) {
  const monthlyYearly = monthlyPrice * 12
  if (monthlyYearly <= 0 || annualPrice <= 0)
    return 0
  return Math.round(((monthlyYearly - annualPrice) / monthlyYearly) * 100)
}

/**
 * Simplified pricing plans - all plans have same features, different pricing only
 */
export const pricingPlans = [
  {
    id: 'monthly',
    name: 'Mensuel',
    description: 'Facturation mensuelle',
    priceId: {
      stripe: appConfigs.stripeMonthlyPriceId,
    },
    mode: 'subscription',
    price: 59,
    billing: 'monthly',
    features: ['Accès complet à toutes les fonctionnalités'],
    cta: 'Choisir ce plan',
    highlighted: false,
    popular: false,
  },
  {
    id: 'annual',
    name: 'Annuel',
    description: 'Facturation annuelle ',
    priceId: {
      stripe: appConfigs.stripeAnnualPriceId,
    },
    mode: 'subscription',
    price: 659, //
    billing: 'annual',
    features: [
      'Accès complet à toutes les fonctionnalités',
      'Support client prioritaire',
      'Épargnez par rapport au plan mensuel',
    ],
    cta: 'Choisir ce plan', // "Économiser maintenant",

    savings: getSavingsPercent(59, 659),
  },
  {
    id: 'lifetime',
    name: 'À vie',
    description: 'Paiement unique - Accès permanent',
    priceId: {
      stripe: appConfigs.stripeLifetimePriceId,
    },

    mode: 'payment',
    price: 895,
    billing: 'lifetime',
    features: [
      'Accès complet à toutes les fonctionnalités',
      'Support client prioritaire',
      'Accès à vie sans frais récurrents',
    ],
    cta: 'Achat unique',

    highlighted: true,
    popular: true,
    isLifetime: true,
  },
]

/**
 * Get plan by ID
 * @param {string} planId - The plan identifier
 * @returns {object | null} - The matching plan or null if not found
 */
export function getPlanById(planId) {
  return pricingPlans.find(plan => plan.id === planId) || null
}

/**
 * Get plan by Stripe price ID
 * @param {string} priceId - Stripe price ID to look up
 * @returns {object | null} - The matching plan or null if not found
 */
export function getPlanByPriceId(priceId) {
  if (!priceId)
    return null
  return pricingPlans.find(plan => plan.priceId.stripe === priceId) || null
}

/**
 * Create a checkout URL for a specific plan
 * @param {string} priceId - Stripe price ID
 * @param {string} returnUrl - URL to return to after checkout
 * @param {object} trpc - TRPC client instance
 * @returns {Promise<object>} - Checkout response with URL
 */
export async function createCheckoutUrl(priceId, returnUrl, trpc) {
  try {
    const plan = getPlanByPriceId(priceId)
    const mode = plan?.mode || 'subscription'

    return await trpc.stripe.createCheckoutSession.mutate({
      priceId,
      quantity: 1,
      mode,
      successUrl: `${returnUrl}?billing_success=true`,
      cancelUrl: `${returnUrl}?billing_canceled=true`,
    })
  }
  catch (error) {
    console.error('Failed to create checkout session:', error)
    throw error
  }
}

/**
 * Get Stripe price ID for a plan
 * @param {string} planId - The plan identifier
 * @returns {string|null} - The Stripe price ID or null if plan not found
 */
export function getStripePriceId(planId) {
  const plan = getPlanById(planId)
  return plan ? plan.priceId.stripe : null
}

/**
 * Create a customer portal URL
 * @param {string} returnUrl - URL to return to after using the portal
 * @param {object} trpc - TRPC client instance
 * @returns {Promise<object>} - Portal response with URL
 */
export async function createPortalUrl(returnUrl, trpc) {
  try {
    return await trpc.stripe.createPortalSession.mutate({
      returnUrl,
    })
  }
  catch (error) {
    console.error('Failed to create portal session:', error)
    throw error
  }
}
